import Image from 'components/Common/Image'
import ImageHero from 'components/Common/ImageHero'
import HeadData from 'components/Common/HeadData'
import OfferList from 'components/OfferList/OfferList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useContext } from 'react'
import styled from 'styled-components'
import { StepsHowItWorks, getStepsBuyNow, getTermsAndConditionsUrlLink } from './AtomeLandingPageData'
import { connect } from 'react-redux'
import Error404Page from 'components/Pages/Error404Page'
import TextButton from 'LuxKit/Button/TextButton'
import { isAtomeBpEnabled } from 'lib/payment/atomeBpUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Subtitle from 'LuxKit/Typography/Subtitle'
import TextLink from 'LuxKit/TextLink'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import GeoContext from 'contexts/geoContext'

const HowItWorksContent = styled.div`
  margin-top: ${rem(40)};
  width: 100%;
`

const BuyNowContent = styled.div`
  margin-top: ${rem(48)};
  width: 100%;
`

const AppStoreContent = styled.div`
  margin-top: ${rem(20)};
  width: 100%;
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(40)};

    &.large-margin {
      margin-top: ${rem(92)};
    }
  }
`

const InnerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(32)} ${rem(40)};
  max-width: ${rem(1160)};
  width: 100%;
  height: 100%;

  ${mediaQueryUp.desktop} {
    padding: ${rem(60)} ${rem(32)};
  }
`

const HeroImage = styled(ImageHero)`
  max-height: ${rem(480)};
`

const LogoImage = styled(Image)`
  margin-bottom: 1px;
  image-rendering: -webkit-optimize-contrast;
  transform: translateZ(0);
`

const StoreImage = styled(Image)`
  margin-top: ${rem(20)};
  margin-right: ${rem(6)};
  margin-left: ${rem(6)};
`

const HeroSectionContent = styled(InnerSection)`
  align-items: center;
  justify-content: center;
  position: relative;
`

const Section = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  max-width: 100%;

  &:nth-child(even) {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }
`

const SectionCTA = styled(TextButton)`
  margin-top: ${rem(32)};
`

const SectionBlurb = styled(Subtitle).attrs<any>({
  align: 'center',
  colour: 'neutral-eight',
})`
  margin-top: ${rem(16)};
  max-width: ${rem(750)};
  width: 100%;
`

const ProductDescriptors = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    margin-left: -${rem(30)};
  }
`

const ProductDescriptor = styled.div`
  display: flex;
  flex-direction: column;
  /* Fixes IE overflow issue */
  align-items: stretch;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.six};
  padding: ${rem(24)};
  margin-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    margin-left: ${rem(30)};
    margin-top: 0;
    height: ${rem(180)};
    flex: 1 1 33%;
  }
`

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  > * + * {
    margin-top: ${rem(16)};

    ${mediaQueryUp.tablet} {
      margin-top: 0;
    }
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    margin-left: -${rem(32)};
  }
`

const HowItWorksCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: relative;
  padding: ${rem(110)} ${rem(32)} ${rem(32)};
  max-width: ${rem(415)};
  flex-grow: 1;
  margin-left: ${rem(32)};

  ${mediaQueryUp.tablet} {
    flex-basis: 100%;
  }
`

const AppLinks = styled(TextLink)`
  display: block;
  padding: 0;
`

const HowItWorksCardImage = styled.div`
  position: absolute;
  top: ${rem(40)};
  left: 0;
  right: 0;
  height: ${rem(40)};
  display: flex;
  align-items: center;
  justify-content: center;
`
interface Props {
  currency: string;
  theme: any;
  isAtomeBpEnabled: boolean;
}

const listFilters: App.OfferListFilters = {
  offerTypes: FLASH_OFFERS_TYPES,
  frontPageVisibleOnly: true,
}

function AtomeLandingPage(props: Props) {
  React.useEffect(() => {
    window.scroll(0, 0)
  }, [])

  const {
    currency,
    isAtomeBpEnabled,
  } = props

  const { currentRegionCode } = useContext(GeoContext)

  const stepsHowItWorks = StepsHowItWorks || []
  const stepsBuyNow = getStepsBuyNow(currency, currentRegionCode) || []
  const termsAndConditionsLink = getTermsAndConditionsUrlLink(currency)

  if (!isAtomeBpEnabled) {
    return <Error404Page invalidRegion />
  }

  return (
    <div>
      <HeadData title="Atome" />
      <HeroImage
        alt=""
        aria-hidden="true"
        gravity="auto"
        mobileAspectRatio="8:9"
        tabletAspectRatio="2:1"
        desktopAspectRatio="8:3"
        largeDesktopAspectRatio="16:5"
        id="atome-background"
        brightness={-30}
      >
        <HeroSectionContent>
          <LogoImage
            height="130"
            id="atome-logo-yellow"
            alt="Atome"
            highRes
            format="svg"
            quality="good"
          />
          <Heading variant="heading3" align="center" colour="neutral-eight">Time to own it.</Heading>
          <SectionBlurb variant="subtitle2" colour="neutral-eight">
            3 easy payments, 0% interest.
          </SectionBlurb>
        </HeroSectionContent>
      </HeroImage>
      <Section>
        <InnerSection>
          <Heading variant="heading2" align="center">How it works</Heading>
          <HowItWorksContent>
            <ColumnContainer>
              {stepsHowItWorks.map((step, index) => (
                <HowItWorksCard key={index}>
                  <HowItWorksCardImage>{step.image}</HowItWorksCardImage>
                  <BodyText variant="large" weight="bold">{step.title}</BodyText>
                  <BodyText variant="medium">
                    {step.description}
                  </BodyText>
                </HowItWorksCard>
              ))}
            </ColumnContainer>
            <SubSection>
              <BodyText variant="small" align="center">
                Read Atome's full&nbsp;
                <TextLink weight="regular" target="_blank" href={termsAndConditionsLink}>
                  Terms & Conditions
                </TextLink>
              </BodyText>
            </SubSection>
          </HowItWorksContent>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3">Get the Luxury Escape you need now</Heading>
          <BuyNowContent>
            <ProductDescriptors>
              {stepsBuyNow.map((step, index) => (
                <ProductDescriptor key={index}>
                  <Heading variant="heading3">{step.title}</Heading>
                  <BodyText variant="medium">{step.description}</BodyText>
                </ProductDescriptor>
              ))}
            </ProductDescriptors>
          </BuyNowContent>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3" align="center">Use the LE app to buy now and pay later!</Heading>
          <AppStoreContent>
            <BodyText variant="large" align="center">Manage your existing orders with the easy-to-use app.</BodyText>
            <ColumnContainer>
              <AppLinks href="https://app.luxuryescapes.com/3WHB/1aab092" target="_blank" rel="noopener">
                <StoreImage id="qy1f0pkl6sbukmkbwayf" height="48" quality="good" format="svg" />
              </AppLinks>
              <AppLinks href="https://app.luxuryescapes.com/3WHB/feb04c69" target="_blank" rel="noopener">
                <StoreImage id="p0qt65l8f6tzobhdkh" height="48" quality="good" format="svg" />
              </AppLinks>
            </ColumnContainer>
          </AppStoreContent>

        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <SubSection>
            <Subtitle variant="subtitle1" align="center" as="h2">Deals of the week</Subtitle>
            <BuyNowContent>
              <OfferList maxOffers={4} filters={listFilters}/>
            </BuyNowContent>
            <SectionCTA kind="primary" href="/">
              Discover all amazing deals
            </SectionCTA>
          </SubSection>
        </InnerSection>
      </Section>
    </div>
  )
}

const mapStateToProps = (state: App.State) => {
  const currency = state.geo.currentCurrency

  return {
    currency,
    isAtomeBpEnabled: isAtomeBpEnabled(currency),
  }
}

export default connect(mapStateToProps)(AtomeLandingPage)
